/**
 * MiniPlayer — the bar above the tabs: what is playing, play and pause, the next track, the
 * queue, and the slider.
 *
 * Task 07. `spec.md` «Воспроизведение», story 31: «мини-плеер внизу на всех вкладках, чтобы
 * управлять музыкой не уходя с экрана». It sits in the frame between the screen and the tabs (`app/Shell.tsx`), so
 * no tab can cover it and none has to make room for it. Hidden until something has been
 * played: an empty player is a bar of nothing on every screen.
 *
 * It reads `store/player.ts` and calls `engine/player.ts`, and nothing else; it never touches
 * the element. The slider's travel is the whole track, and a drag past what the phone could
 * load stops where the loaded part ends (`engine/readings.ts`, «How far the slider may go»);
 * the loaded part is drawn so the stop is not a surprise.
 *
 * ## «Следующий» and «Очередь» read the queue (task 08)
 *
 * The next track is the queue's (`store/queue.ts`), so the button is off exactly when the
 * queue has nothing after this track — at its end without «repeat». The queue sheet opens
 * from here because this bar is the one thing on every tab.
 *
 * ## What it says when something is wrong
 *
 * One sentence and one button, never a code: `spec.md` «Воспроизведение», story 32, asks for
 * «понятную ошибку с кнопкой „повторить“». While a track is loading the second line says so instead of the
 * artist — that is the loading indicator of the checklist. The sentence, the slider and the
 * buttons' words live in `components/PlayerControls.tsx`, shared with «Сейчас играет».
 *
 * ## The cover and the title open «Сейчас играет» (task 09)
 *
 * The bar is the one thing on every tab, so it is the door to the record, like it is the door
 * to the queue. The door is the whole left part — cover and title — because that is what a
 * thumb aims at when it wants «more of this track».
 *
 * ## Whether «Сейчас играет» is open is the shell's (task 15)
 *
 * A launch from a link opens the screen with no tap on this bar (`app/deeplink.ts`), so the shell
 * holds the open state and hands it in; the bar still draws the screen and opens it on a tap.
 *
 * ## The tap that opens «Сейчас играет» wakes the scratch (task 18)
 *
 * The sound of the scratch is an audio context, and a context is let out of its silence only
 * inside a tap. This is the tap: `engine/scratch.ts` makes the context here and puts it to sleep
 * at once, so a finger on the record can wake it later without one.
 *
 * ## The slider on top (task 41), a gold line (task 67)
 *
 * The time runs along the bar's top edge, as the edge itself: under the title it stood right on
 * the gold line of the tab held down, and the owner read the two as one (2026-10-04). Since task
 * 67 it is only a line, without the figures (`MiniSeek`); the buttons sit over its lower half, so
 * a finger on a button is a tap on the button.
 *
 * ## The heart, right of the title (task 11)
 *
 * «В избранное» for the track playing, next to what it is about. Four round buttons now share
 * the bar with the title, so they stand a step closer (`styles/player.css`); each still has its
 * whole 44 pixels.
 */

import type { JSX } from 'react';
import { useCallback, useState } from 'react';

import { player } from '../engine/player';
import { telegram } from '../lib/telegram';
import { scratch } from '../engine/scratch';
import { NowPlaying } from '../screens/NowPlaying';
import type { Collection } from '../store/library';
import { usePlayer } from '../store/player';

import { Cover } from './Cover';
import { FavoriteButton } from './FavoriteButton';
import {
  loadingText,
  MiniSeek,
  NEXT_LABEL,
  NextIcon,
  PAUSE_LABEL,
  PLAY_LABEL,
  PauseIcon,
  PlayIcon,
  ProblemLine,
  QUEUE_BUTTON_LABEL,
  QueueIcon,
  useHasNext,
  usePlaybackFlags,
} from './PlayerControls';
import { QueueSheet } from './QueueSheet';

export const MINI_PLAYER_LABEL = 'Сейчас играет';
export const OPEN_NOW_PLAYING_LABEL = 'Открыть «Сейчас играет»';

export interface MiniPlayerProps {
  /** «Сейчас играет» is open. */
  readonly nowOpen: boolean;
  readonly onNowOpenChange: (open: boolean) => void;
  /** Task 50: the artist and the riddim of «Сейчас играет» open their lists through the shell. */
  readonly onOpenCollection?: ((collection: Collection) => void) | undefined;
}

export function MiniPlayer({
  nowOpen,
  onNowOpenChange: setNowOpen,
  onOpenCollection,
}: MiniPlayerProps): JSX.Element | null {
  const track = usePlayer((s) => s.track);
  const resumeFromS = usePlayer((s) => s.resumeFromS);
  const hasNext = useHasNext();
  const { playing, loading } = usePlaybackFlags();
  const [queueOpen, setQueueOpen] = useState(false);
  const closeQueue = useCallback(() => {
    setQueueOpen(false);
  }, []);
  const closeNow = useCallback(() => {
    setNowOpen(false);
  }, [setNowOpen]);

  if (track === undefined) return null;

  return (
    <>
      <section className="mini" aria-label={MINI_PLAYER_LABEL}>
        <MiniSeek />
        <div className="miniRow">
          <button
            type="button"
            className="miniOpen"
            aria-label={OPEN_NOW_PLAYING_LABEL}
            aria-haspopup="dialog"
            onClick={() => {
              scratch().prime();
              setNowOpen(true);
            }}
          >
            <Cover src={track.cover} className="miniCover" />
            <span className="miniText">
              <span className="miniTitle">{track.title}</span>
              {loading ? (
                <span className="miniByline" role="status">
                  {loadingText(resumeFromS)}
                </span>
              ) : (
                track.performer !== '' && <span className="miniByline">{track.performer}</span>
              )}
            </span>
          </button>
          <FavoriteButton
            trackId={track.id}
            title={track.title}
            className="miniButton miniFav"
            iconClassName="miniIcon"
          />
          <button
            type="button"
            className={loading ? 'miniButton miniPlay miniButtonBusy' : 'miniButton miniPlay'}
            aria-label={playing ? PAUSE_LABEL : PLAY_LABEL}
            onClick={() => {
              // Task 50: a light knock, like the deck's keys.
              telegram.haptic('impact');
              player().toggle();
            }}
          >
            {playing ? <PauseIcon className="miniIcon" /> : <PlayIcon className="miniIcon" />}
          </button>
          <button
            type="button"
            className="miniButton miniNext"
            aria-label={NEXT_LABEL}
            disabled={!hasNext}
            onClick={() => {
              player().next();
            }}
          >
            <NextIcon className="miniIcon" />
          </button>
          <button
            type="button"
            className="miniButton"
            aria-label={QUEUE_BUTTON_LABEL}
            aria-haspopup="dialog"
            onClick={() => {
              setQueueOpen(true);
            }}
          >
            <QueueIcon className="miniIcon" />
          </button>
        </div>

        <ProblemLine />
      </section>
      {queueOpen && <QueueSheet onClose={closeQueue} />}
      {nowOpen && <NowPlaying onClose={closeNow} onOpenCollection={onOpenCollection} />}
    </>
  );
}
